import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import SearchIcon from 'icons/Search.icon';
import { Input, Loader } from 'ncoded-component-library';
import { useLocation } from 'react-router-dom';
import { SEARCH_ROUTE } from 'data/global';

import './SearchForm.styles.scss';
import './SearchForm.styles.responsive.scss';

type SearchFormProps = {
  className?: string;
  isLoading?: boolean;
  onChange?: (value: string) => void;
};

const SearchForm: FC<SearchFormProps> = ({
  className,
  isLoading,
  onChange,
}) => {
  const {
    params: { searchString },
    setQueryParam,
    setParamsAndNavigate,
    removeAllQueryParams,
  } = useQueryParams();

  const [inputSearchValue, setInputSearchValue] = useState(searchString || '');

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const currentRouteIsSearch = useMemo(
    () => pathname === SEARCH_ROUTE,
    [pathname],
  );

  const searchBy = useCallback(
    (searchString: string) => {
      removeAllQueryParams();

      if (currentRouteIsSearch) {
        setQueryParam('searchString', searchString);
      } else {
        setParamsAndNavigate(SEARCH_ROUTE, { searchString });
      }
    },
    [
      currentRouteIsSearch,
      removeAllQueryParams,
      setParamsAndNavigate,
      setQueryParam,
    ],
  );

  const onSearchChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const value = ev.target.value;

      setInputSearchValue(value);
      onChange?.(value);
    },
    [onChange],
  );

  const onSearchSubmit = useCallback(
    (ev) => {
      ev.preventDefault();

      const formData = new FormData(ev.target);
      const searchString = formData.get('search')?.toString() || '';

      searchBy(searchString);
    },
    [searchBy],
  );

  const onSearchReset = useCallback(() => {
    setInputSearchValue('');
    searchBy('');
  }, [searchBy]);

  useEffect(() => {
    setInputSearchValue(searchString || '');
  }, [pathname, searchString]);

  return (
    <div className={classNames('search-form', className)}>
      <form
        onSubmit={onSearchSubmit}
        onReset={onSearchReset}
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          name="search"
          autoComplete="off"
          prefixNode={<SearchIcon />}
          placeholder={t('search')}
          value={inputSearchValue}
          {...(isLoading && { suffixNode: <Loader inline size="small" /> })}
          onChange={onSearchChange}
        />
      </form>
    </div>
  );
};

export default SearchForm;
